import { FC, useState } from 'react'
import { useTranslate } from 'react-admin'
import { Box, Chip, IconButton, Table, TableBody, TableCell, TableContainer, TableRow, Typography } from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { border } from '../layout/theme/border'
import { xxllncColor } from '../layout/theme/color'
import { fadeSlideIn } from './dashboard'


export const InfoRow: FC<{
  label: string; value: React.ReactNode; mono?: boolean
}> = ({ label, value, mono }) => (
  <Box sx={{
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', py: 1.25,
    borderBottom: '1px solid rgba(0,0,0,0.05)',
  }}>
    <Typography sx={{ fontSize: 12, fontWeight: 500, color: 'rgba(0,0,0,0.45)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#1a1a1a', fontFamily: mono ? 'monospace' : 'inherit' }}>
      {value ?? '-'}
    </Typography>
  </Box>
)

export const StatusChip: FC<{ active: boolean }> = ({ active }) => {
  const translate = useTranslate()
  return (
    <Chip
      icon={active
        ? <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />
        : <HighlightOffIcon sx={{ fontSize: 14 }} />
      }
      label={active ? translate('data.statusAvailable') : translate('data.statusInactive')}
      size="small"
      sx={{
        fontWeight: 600, fontSize: 11, height: 22, border: 'none',
        bgcolor: active ? xxllncColor.opGreen10 : 'rgba(0,0,0,0.06)',
        color: active ? xxllncColor.green100 : 'rgba(0,0,0,0.4)',
      }}
    />
  )
}

export const DetailCard: FC<{
  title: string
  delay?: number
  children: React.ReactNode
}> = ({ title, delay = 0, children }) => (
  <Box
    sx={{
      background: '#fff',
      borderRadius: border.card.radius,
      border: '1px solid rgba(0,0,0,0.06)',
      p: 3,
      animation: `${fadeSlideIn} 0.5s ease-out ${delay}ms both`,
    }}
  >
    <Typography sx={{
      fontSize: 11, fontWeight: 600, color: 'rgba(0,0,0,0.35)',
      textTransform: 'uppercase', letterSpacing: '0.06em', mb: 2,
    }}>
      {title}
    </Typography>
    {children}
  </Box>
)

const MetadataValue: FC<{ value: string }> = ({ value }) => {
  const [visible, setVisible] = useState(false)
  const masked = value.length > 4
    ? `${'•'.repeat(Math.min(value.length, 20))}${value.slice(-4)}`
    : '••••'

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography sx={{ fontSize: 12, color: '#1a1a1a', fontFamily: 'monospace' }}>
        {visible ? value : masked}
      </Typography>
      <IconButton size="small" onClick={() => setVisible((v) => !v)} sx={{ p: 0.25 }}>
        {visible
          ? <VisibilityOffIcon sx={{ fontSize: 14, color: 'rgba(0,0,0,0.3)' }} />
          : <VisibilityIcon sx={{ fontSize: 14, color: 'rgba(0,0,0,0.3)' }} />
        }
      </IconButton>
    </Box>
  )
}

export const MetadataTable: FC<{ entries: [string, string][] }> = ({ entries }) => (
  <TableContainer>
    <Table size="small">
      <TableBody>
        {entries.map(([key, value]) => (
          <TableRow key={key} sx={{ '&:last-child td': { borderBottom: 0 } }}>
            <TableCell sx={{
              pl: 0, fontSize: 12, fontWeight: 600,
              color: 'rgba(0,0,0,0.55)', fontFamily: 'monospace',
              borderColor: 'rgba(0,0,0,0.05)',
            }}>
              {key}
            </TableCell>
            <TableCell sx={{ pr: 0, borderColor: 'rgba(0,0,0,0.05)' }}>
              <MetadataValue value={value} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
)

export const ToolSourceChip: FC<{ source: string }> = ({ source }) => {
  const translate = useTranslate()
  const label = source === 'manual'
    ? translate('data.toolSourceManual')
    : source === 'openapi'
      ? translate('data.toolSourceOpenapi')
      : source === 'odata'
        ? translate('data.toolSourceOdata')
        : source
  const isAuto = source === 'openapi' || source === 'odata'
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        fontWeight: 600, fontSize: 11, height: 22, border: 'none',
        bgcolor: isAuto ? xxllncColor.opDark10 : 'rgba(0,0,0,0.06)',
        color: isAuto ? xxllncColor.dark100 : 'rgba(0,0,0,0.55)',
      }}
    />
  )
}

export const StatCard: FC<{ value: number; label: string; accent: string; delay: number }> = ({ value, label, accent, delay }) => (
  <Box
    sx={{
      'background': '#fff',
      'borderRadius': border.card.radius,
      'border': '1px solid rgba(0,0,0,0.06)',
      'p': 2.5,
      'animation': `${fadeSlideIn} 0.5s ease-out ${delay}ms both`,
      'transition': 'transform 0.2s ease',
      '&:hover': { transform: 'translateY(-1px)' },
    }}
  >
    <Typography sx={{ fontSize: 11, fontWeight: 600, color: `${accent}99`, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 28, fontWeight: 700, color: accent, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
      {value.toLocaleString('nl-NL')}
    </Typography>
  </Box>
)

export const formatDateTime = (d: string | Date): string =>
  new Date(d).toLocaleDateString('nl-NL', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  })
